import { ManagedCampaignData } from "@/components/managed-campaign/ManagedCampaignData";
import { ManagedReviewStep } from "@/components/managed-campaign/ManagedReviewStep";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Check, Loader2, Clock, Mail, Users, BarChart3 } from "lucide-react";

interface SimplifiedReviewStepProps {
    data: ManagedCampaignData;
    clientName?: string;
    isSubmitting: boolean;
    onSubmit: () => void;
    onBack: () => void;
    isGuest?: boolean;
    onNameChange?: (name: string) => void;
}

export const SimplifiedReviewStep = ({
    data,
    clientName,
    isSubmitting,
    onSubmit,
    onBack,
    isGuest,
    onNameChange
}: SimplifiedReviewStepProps) => {
    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="space-y-4">
                <h2 className="text-2xl font-bold tracking-tight">Review & Launch</h2>
                <p className="text-muted-foreground">Review your campaign settings before launching.</p>
            </div>

            {!isGuest && onNameChange && (
                <div className="max-w-md space-y-2">
                    <Label htmlFor="reviewCampaignName" className="text-sm font-medium">Campaign name</Label>
                    <Input id="reviewCampaignName" value={data.campaignName} onChange={(e) => onNameChange(e.target.value)} placeholder="Name your campaign" />
                    <p className="text-xs text-muted-foreground">Filled in from your campaign strategy. You can change it.</p>
                </div>
            )}

            <ManagedReviewStep data={data} clientName={clientName} hideDailyLimits={isGuest} scheduleCardTitle={isGuest ? "Schedule" : undefined} />

            <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={onBack} disabled={isSubmitting} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </Button>
                <Button onClick={onSubmit} disabled={isSubmitting || (!isGuest && !!onNameChange && !data.campaignName?.trim())} size="lg" className="gap-2 min-w-[140px]">
                    {isSubmitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {isGuest ? "Submitting..." : "Launching..."}
                        </>
                    ) : (
                        <>
                            <Check className="h-4 w-4" />
                            {isGuest ? "Submit" : "Launch Campaign"}
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
};

export const GuestThankYouView = () => (
    <div className="flex items-center justify-center min-h-[60vh] animate-in fade-in duration-700">
        <div className="bg-card border border-border/50 rounded-xl p-8 max-w-lg w-full text-center shadow-sm">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Request Submitted!</h2>
            <p className="text-muted-foreground">
                Thank you for completing your campaign setup. Our team will review your request and get back to you shortly.
            </p>
        </div>
    </div>
);
